import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

type Product = {
  id: string;
  name: string;
  brand: string;
  category: string;
  gender: string;
  price: number;
  sizes: string[];
  sizeBarcodes: Record<string, string>;
  sizeIds: Record<string, string>;
  sizeStock: Record<string, number>;
  color?: string;
  imageUrl: string;
};

type CartItem = {
  productId: string;
  name: string;
  price: number;
  imageUrl: string;
  size: string;
  quantity: number;
};

type Promotion = {
  id: string;
  name: string;
  imageSrc: string;
};

type CatalogCachePayload = {
  products: Product[];
  promotions: Promotion[];
  promotionProductMap: Record<string, string[]>;
  savedAt: number;
};

type ProductQueryFilters = {
  category?: string;
  gender?: string;
  brand?: string;
  size?: string;
  productIds?: string[];
  excludeProductId?: string;
};

type FilterDropdownProps = {
  label: string;
  value: string;
  placeholder: string;
  options: string[];
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onSelect: (value: string) => void;
};
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl as string, supabaseKey as string) : null;
const CART_STORAGE_KEY = 'fashion-style-cart';
const SHARED_CART_QUERY_PARAM = 'cart';
const SHARED_APP_URL = 'https://catalogofashionstyle.vercel.app';
const CATALOG_CACHE_KEY = 'fashion-style-catalog-cache-v2';
const CATALOG_CACHE_TTL_MS = 1000 * 60 * 10;
const PRODUCT_ORDER_CACHE_KEY = 'fashion-style-product-order-cache-v2';
const PRODUCT_ORDER_CACHE_TTL_MS = 1000 * 60 * 30;

let inMemoryCatalogCache: CatalogCachePayload | null = null;
let catalogLoadPromise: Promise<CatalogCachePayload> | null = null;


function normalizeImageUrl(u: string | null | undefined) {
  const s = (u || '').replace(/`/g, '').trim();
  if (!s) return '';
  if (!/^https?:\/\//.test(s)) return '';
  if (s.includes('res.cloudinary.com/') && s.includes('/image/upload/')) {
    if (s.includes('/image/upload/f_auto,q_auto/')) return s;
    return s.replace('/image/upload/', '/image/upload/f_auto,q_auto/');
  }
  return s;
}

function readCatalogCache() {
  if (inMemoryCatalogCache && (Date.now() - inMemoryCatalogCache.savedAt) < CATALOG_CACHE_TTL_MS) {
    return inMemoryCatalogCache;
  }

  if (typeof window === 'undefined') return null;

  try {
    const rawValue = window.localStorage.getItem(CATALOG_CACHE_KEY);
    if (!rawValue) return null;
    const parsedValue = JSON.parse(rawValue) as CatalogCachePayload;
    if (!parsedValue?.savedAt || (Date.now() - parsedValue.savedAt) >= CATALOG_CACHE_TTL_MS) {
      window.localStorage.removeItem(CATALOG_CACHE_KEY);
      return null;
    }
    inMemoryCatalogCache = parsedValue;
    return parsedValue;
  } catch {
    return null;
  }
}

function writeCatalogCache(payload: Omit<CatalogCachePayload, 'savedAt'>) {
  if (typeof window === 'undefined') return;

  try {
    const nextPayload: CatalogCachePayload = { ...payload, savedAt: Date.now() };
    inMemoryCatalogCache = nextPayload;
    window.localStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify(nextPayload));
  } catch {
    inMemoryCatalogCache = { ...payload, savedAt: Date.now() };
  }
}

function buildProductQueryCacheKey(filters: ProductQueryFilters) {
  return JSON.stringify({
    category: filters.category || '',
    gender: filters.gender || '',
    brand: filters.brand || '',
    size: filters.size || '',
    productIds: [...(filters.productIds || [])].sort(),
    excludeProductId: filters.excludeProductId || '',
  });
}

function readProductOrderCache(cacheKey: string) {
  if (typeof window === 'undefined') return null;

  try {
    const rawValue = window.localStorage.getItem(PRODUCT_ORDER_CACHE_KEY);
    if (!rawValue) return null;
    const parsedValue = JSON.parse(rawValue) as Record<string, { ids: string[]; savedAt: number }>;
    const cacheEntry = parsedValue[cacheKey];
    if (!cacheEntry?.savedAt || (Date.now() - cacheEntry.savedAt) >= PRODUCT_ORDER_CACHE_TTL_MS) {
      delete parsedValue[cacheKey];
      window.localStorage.setItem(PRODUCT_ORDER_CACHE_KEY, JSON.stringify(parsedValue));
      return null;
    }
    const uniqueIds = Array.from(new Set((cacheEntry.ids || []).map((id) => String(id || '').trim()).filter(Boolean)));
    if (uniqueIds.length !== cacheEntry.ids.length) {
      parsedValue[cacheKey] = {
        ids: uniqueIds,
        savedAt: cacheEntry.savedAt,
      };
      window.localStorage.setItem(PRODUCT_ORDER_CACHE_KEY, JSON.stringify(parsedValue));
    }
    return uniqueIds;
  } catch {
    return null;
  }
}

function writeProductOrderCache(cacheKey: string, ids: string[]) {
  if (typeof window === 'undefined') return;

  try {
    const rawValue = window.localStorage.getItem(PRODUCT_ORDER_CACHE_KEY);
    const parsedValue = rawValue ? JSON.parse(rawValue) as Record<string, { ids: string[]; savedAt: number }> : {};
    parsedValue[cacheKey] = {
      ids,
      savedAt: Date.now(),
    };
    window.localStorage.setItem(PRODUCT_ORDER_CACHE_KEY, JSON.stringify(parsedValue));
  } catch {
  }
}

function colorDistance(a: [number, number, number], b: [number, number, number]) {
  const dr = a[0] - b[0];
  const dg = a[1] - b[1];
  const db = a[2] - b[2];
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function toRgba(rgb: [number, number, number], alpha: number) {
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

function easeInOutCubic(t: number) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function animateWindowScroll(targetTop: number, duration: number) {
  const startTop = window.scrollY;
  const distance = targetTop - startTop;
  const startTime = performance.now();

  const step = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeInOutCubic(progress);
    window.scrollTo(0, startTop + distance * easedProgress);
    if (progress < 1) window.requestAnimationFrame(step);
  };

  window.requestAnimationFrame(step);
}

function extractPalette(data: Uint8ClampedArray) {
  const buckets = new Map<string, { count: number; r: number; g: number; b: number }>();

  for (let i = 0; i < data.length; i += 16) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    if (a < 180) continue;

    const key = `${Math.round(r / 24)}-${Math.round(g / 24)}-${Math.round(b / 24)}`;
    const current = buckets.get(key) || { count: 0, r: 0, g: 0, b: 0 };
    current.count += 1;
    current.r += r;
    current.g += g;
    current.b += b;
    buckets.set(key, current);
  }

  const ranked = Array.from(buckets.values())
    .map((bucket) => {
      const rgb: [number, number, number] = [
        Math.round(bucket.r / bucket.count),
        Math.round(bucket.g / bucket.count),
        Math.round(bucket.b / bucket.count),
      ];
      const max = Math.max(...rgb);
      const min = Math.min(...rgb);
      const saturation = max === 0 ? 0 : (max - min) / max;
      const luminance = (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
      const contrast = Math.abs(luminance - 0.5);
      const score = bucket.count * (0.45 + saturation * 1.4 + contrast);
      return { rgb, score, luminance };
    })
    .sort((a, b) => b.score - a.score);

  const selected: [number, number, number][] = [];

  for (const item of ranked) {
    if (selected.every((picked) => colorDistance(picked, item.rgb) > 70)) {
      selected.push(item.rgb);
    }
    if (selected.length === 3) break;
  }

  const fallback: [number, number, number][] = [
    [255, 199, 248],
    [178, 212, 255],
    [217, 254, 215],
  ];

  while (selected.length < 3) {
    selected.push(fallback[selected.length]);
  }

  return selected;
}

function compareSizes(a: string, b: string) {
  const normalizedA = String(a || '').trim().toUpperCase();
  const normalizedB = String(b || '').trim().toUpperCase();
  const sizeOrder = ['XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  const sizeOrderMap = new Map(sizeOrder.map((size, index) => [size, index]));
  const rankA = sizeOrderMap.get(normalizedA);
  const rankB = sizeOrderMap.get(normalizedB);
  const isNumberA = /^-?\d+(\.\d+)?$/.test(normalizedA);
  const isNumberB = /^-?\d+(\.\d+)?$/.test(normalizedB);

  if (isNumberA && isNumberB) {
    return Number(normalizedA) - Number(normalizedB);
  }

  if (rankA !== undefined && rankB !== undefined) {
    return rankA - rankB;
  }

  if (rankA !== undefined) return -1;
  if (rankB !== undefined) return 1;
  if (isNumberA) return -1;
  if (isNumberB) return 1;

  return normalizedA.localeCompare(normalizedB, 'es', { numeric: true });
}

function getSortedUniqueValues(values: string[]) {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

function getSortedUniqueSizes(values: string[]) {
  return Array.from(new Set(values.filter(Boolean))).sort(compareSizes);
}

function shuffleItems<T>(items: T[]) {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }

  return shuffled;
}

function mergeBarcodeMapIntoProducts(products: Product[], barcodeMap: Record<string, string>) {
  if (Object.keys(barcodeMap).length === 0) return products;

  return products.map((product) => {
    let hasChanges = false;
    const nextSizeBarcodes = { ...product.sizeBarcodes };

    product.sizes.forEach((size) => {
      const barcode = barcodeMap[`${product.id}::${size}`];
      if (barcode && nextSizeBarcodes[size] !== barcode) {
        nextSizeBarcodes[size] = barcode;
        hasChanges = true;
      }
    });

    return hasChanges ? { ...product, sizeBarcodes: nextSizeBarcodes } : product;
  });
}

function mergeProductsById(currentProducts: Product[], nextProducts: Product[]) {
  const currentMap = new Map(currentProducts.map((product) => [product.id, product]));

  nextProducts.forEach((product) => {
    const currentProduct = currentMap.get(product.id);
    if (!currentProduct) {
      currentMap.set(product.id, product);
      return;
    }

    currentMap.set(product.id, {
      ...currentProduct,
      ...product,
      sizeBarcodes: { ...currentProduct.sizeBarcodes, ...product.sizeBarcodes },
      sizeIds: { ...currentProduct.sizeIds, ...product.sizeIds },
      sizeStock: { ...currentProduct.sizeStock, ...product.sizeStock },
      sizes: getSortedUniqueSizes([...currentProduct.sizes, ...product.sizes]),
    });
  });

  return Array.from(currentMap.values());
}

function normalizeProducts(rows: any[]) {
  const normalizedProducts = (rows || [])
    .map((p: any) => {
      const img = normalizeImageUrl(p.image_url);
      const sizeItems = Array.isArray(p.product_sizes)
        ? p.product_sizes.filter((s: any) => s && typeof s.stock === 'number' && s.stock > 0)
        : [];
      const uniqueSizes = getSortedUniqueSizes(sizeItems.map((s: any) => String(s.size)));
      const sizeIds = sizeItems.reduce((acc: Record<string, string>, s: Record<string, any>) => {
        const size = String(s.size || '').trim();
        const sizeId = String(s.id || '').trim();
        if (size && sizeId && !acc[size]) {
          acc[size] = sizeId;
        }
        return acc;
      }, {});
      const sizeStock = sizeItems.reduce((acc: Record<string, number>, s: Record<string, any>) => {
        const size = String(s.size || '').trim();
        const stock = typeof s.stock === 'number' ? s.stock : Number(s.stock || 0);
        if (size) {
          acc[size] = stock;
        }
        return acc;
      }, {});
      return img && uniqueSizes.length > 0 ? {
        id: String(p.id || ''),
        name: p.name || '',
        brand: p.brand || '',
        category: p.category || '',
        gender: p.gender || '',
        price: typeof p.price === 'string' ? parseFloat(p.price) : (p.price || 0),
        sizes: uniqueSizes,
        sizeBarcodes: {},
        sizeIds,
        sizeStock,
        color: (p.color || ''),
        imageUrl: img,
      } as Product : null;
    })
    .filter(Boolean) as Product[];

  return mergeProductsById([], normalizedProducts);
}

function buildCartShareEntries(items: CartItem[], products: Product[]) {
  return items.flatMap((item) => {
    const product = products.find((productItem) => productItem.id === item.productId);
    if (!product) return [];
    const barcode = String(product.sizeBarcodes[item.size] || '').trim();
    const sizeId = String(product.sizeIds[item.size] || '').trim();
    const code = /^\d+$/.test(barcode) ? barcode : sizeId;
    if (!code) return [];
    return [{ code, quantity: item.quantity }];
  });
}

function buildCartShareUrl(entries: Array<{ code: string; quantity: number }>) {
  if (entries.length === 0) return '';
  const url = new URL('/', SHARED_APP_URL);
  url.searchParams.set(SHARED_CART_QUERY_PARAM, JSON.stringify(entries));
  return url.toString();
}

async function fetchBarcodeMapForProductIds(productIds: string[]) {
  if (!supabase || productIds.length === 0) return {};

  const barcodeMap: Record<string, string> = {};

  for (let index = 0; index < productIds.length; index += 500) {
    const currentProductIds = productIds.slice(index, index + 500);
    const { data, error } = await supabase
      .from('product_size_barcodes')
      .select('product_id,size,barcode')
      .in('product_id', currentProductIds);

    if (error) {
      console.error(error);
      continue;
    }

    (data || []).forEach((entry: any) => {
      const productId = String(entry.product_id || '').trim();
      const size = String(entry.size || '').trim();
      const barcode = String(entry.barcode || '').trim();
      if (productId && size && barcode) {
        barcodeMap[`${productId}::${size}`] = barcode;
      }
    });
  }

  return barcodeMap;
}

async function fetchBarcodeEntriesByCodes(codes: string[]) {
  if (!supabase || codes.length === 0) return {};

  const barcodeEntries: Record<string, { productId: string; size: string }> = {};

  for (let index = 0; index < codes.length; index += 500) {
    const currentCodes = codes.slice(index, index + 500);
    const { data, error } = await supabase
      .from('product_size_barcodes')
      .select('product_id,size,barcode')
      .in('barcode', currentCodes);

    if (error) {
      console.error(error);
      continue;
    }

    (data || []).forEach((entry: any) => {
      const barcode = String(entry.barcode || '').trim();
      const productId = String(entry.product_id || '').trim();
      const size = String(entry.size || '').trim();
      if (barcode && productId && size) {
        barcodeEntries[barcode] = { productId, size };
      }
    });
  }

  return barcodeEntries;
}

async function fetchProductSizeEntriesByIds(sizeIds: string[]) {
  if (!supabase || sizeIds.length === 0) return {};

  const sizeEntries: Record<string, { productId: string; size: string }> = {};

  for (let index = 0; index < sizeIds.length; index += 500) {
    const currentSizeIds = sizeIds.slice(index, index + 500);
    const { data, error } = await supabase
      .from('product_sizes')
      .select('id,product_id,size')
      .in('id', currentSizeIds);

    if (error) {
      console.error(error);
      continue;
    }

    (data || []).forEach((entry: any) => {
      const sizeId = String(entry.id || '').trim();
      const productId = String(entry.product_id || '').trim();
      const size = String(entry.size || '').trim();
      if (sizeId && productId && size) {
        sizeEntries[sizeId] = { productId, size };
      }
    });
  }

  return sizeEntries;
}

function applyProductFilters(query: any, filters: ProductQueryFilters) {
  let nextQuery = query;

  if (filters.category) {
    nextQuery = nextQuery.eq('category', filters.category);
  }

  if (filters.gender) {
    nextQuery = nextQuery.eq('gender', filters.gender);
  }

  if (filters.brand) {
    nextQuery = nextQuery.eq('brand', filters.brand);
  }

  if (filters.size) {
    nextQuery = nextQuery.eq('product_sizes.size', filters.size);
  }

  if (filters.excludeProductId) {
    nextQuery = nextQuery.neq('id', filters.excludeProductId);
  }

  if (filters.productIds && filters.productIds.length > 0) {
    nextQuery = nextQuery.in('id', filters.productIds);
  }

  return nextQuery;
}

async function fetchMatchingProductIds(filters: ProductQueryFilters = {}, cacheKey?: string) {
  if (!supabase) return [];

  if (cacheKey) {
    const cachedIds = readProductOrderCache(cacheKey);
    if (cachedIds && cachedIds.length > 0) {
      return cachedIds;
    }
  }

  let from = 0;
  const step = 1000;
  const matchingIds: string[] = [];

  while (true) {
    let query = supabase
      .from('products')
      .select('id,image_url,product_sizes!inner(id,size,stock)')
      .gt('product_sizes.stock', 0)
      .neq('image_url', null)
      .range(from, from + step - 1);

    query = applyProductFilters(query, filters);

    const { data, error } = await query;

    if (error) {
      console.error(error);
      break;
    }

    if (!data || data.length === 0) break;

    matchingIds.push(...normalizeProducts(data).map((product) => product.id));
    if (data.length < step) break;
    from += step;
  }

  const shuffledIds = shuffleItems(Array.from(new Set(matchingIds)));

  if (cacheKey && shuffledIds.length > 0) {
    writeProductOrderCache(cacheKey, shuffledIds);
  }

  return shuffledIds;
}

async function fetchProductsByIds(productIds: string[]) {
  if (!supabase || productIds.length === 0) return [];

  const rows: any[] = [];

  for (let index = 0; index < productIds.length; index += 500) {
    const currentProductIds = productIds.slice(index, index + 500);
    const { data, error } = await supabase
      .from('products')
      .select('id,name,brand,category,gender,price,image_url,color,product_sizes!inner(id,size,stock)')
      .in('id', currentProductIds)
      .gt('product_sizes.stock', 0)
      .neq('image_url', null);

    if (error) {
      console.error(error);
      continue;
    }

    rows.push(...(data || []));
  }

  const normalizedProducts = normalizeProducts(rows);
  const orderMap = new Map(productIds.map((id, index) => [id, index]));

  return normalizedProducts.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));
}

async function fetchProductFieldValues(field: 'category' | 'gender' | 'brand', filters: ProductQueryFilters = {}) {
  if (!supabase) return [];

  let from = 0;
  const step = 1000;
  const values: string[] = [];

  while (true) {
    let query = supabase
      .from('products')
      .select(`${field},product_sizes!inner(size,stock)`)
      .gt('product_sizes.stock', 0)
      .neq('image_url', null)
      .range(from, from + step - 1);

    query = applyProductFilters(query, filters);

    const { data, error } = await query;

    if (error) {
      console.error(error);
      break;
    }

    if (!data || data.length === 0) break;

    values.push(...data.map((row: any) => String(row[field] || '').trim()).filter(Boolean));
    if (data.length < step) break;
    from += step;
  }

  return getSortedUniqueValues(values);
}

async function fetchAvailableSizes(filters: ProductQueryFilters = {}) {
  if (!supabase) return [];

  let from = 0;
  const step = 1000;
  const values: string[] = [];

  while (true) {
    let query = supabase
      .from('products')
      .select('product_sizes!inner(size,stock)')
      .gt('product_sizes.stock', 0)
      .neq('image_url', null)
      .range(from, from + step - 1);

    query = applyProductFilters(query, filters);

    const { data, error } = await query;

    if (error) {
      console.error(error);
      break;
    }

    if (!data || data.length === 0) break;

    data.forEach((row: any) => {
      const sizes = Array.isArray(row.product_sizes) ? row.product_sizes : [];
      values.push(...sizes.map((sizeItem: any) => String(sizeItem.size || '').trim()).filter(Boolean));
    });

    if (data.length < step) break;
    from += step;
  }

  return getSortedUniqueSizes(values);
}

async function fetchRelatedProductsForProduct(product: Product) {
  if (!supabase) return [];

  const fetchGroup = async (filters: ProductQueryFilters, limit: number) => {
    let query = supabase
      .from('products')
      .select('id,name,brand,category,gender,price,image_url,color,product_sizes!inner(id,size,stock)')
      .gt('product_sizes.stock', 0)
      .neq('image_url', null)
      .limit(limit);

    query = applyProductFilters(query, filters);

    const { data, error } = await query;

    if (error) {
      console.error(error);
      return [] as Product[];
    }

    return shuffleItems(normalizeProducts(data || []));
  };

  const [sameCategoryAndGender, sameGender, others] = await Promise.all([
    fetchGroup({ category: product.category, gender: product.gender, excludeProductId: product.id }, 18),
    fetchGroup({ gender: product.gender, excludeProductId: product.id }, 18),
    fetchGroup({ excludeProductId: product.id }, 18),
  ]);

  const uniqueProducts = new Map<string, Product>();
  [...sameCategoryAndGender, ...sameGender, ...others].forEach((item) => {
    if (item.id !== product.id && !uniqueProducts.has(item.id)) {
      uniqueProducts.set(item.id, item);
    }
  });

  return Array.from(uniqueProducts.values()).slice(0, 6);
}

async function loadCatalogData() {
  const cachedCatalog = readCatalogCache();
  if (cachedCatalog && cachedCatalog.promotions.length > 0) return cachedCatalog;

  if (!supabase) {
    return {
      products: [],
      promotions: [],
      promotionProductMap: {},
      savedAt: Date.now(),
    };
  }

  if (catalogLoadPromise) return catalogLoadPromise;

  catalogLoadPromise = (async () => {
    const [{ data: promotionsData, error: promotionsError }, { data: promotionProductsData, error: promotionProductsError }] = await Promise.all([
      supabase
        .from('promotions')
        .select('id,name,promotion,is_active')
        .eq('is_active', true)
        .order('created_at', { ascending: false }),
      supabase
        .from('promotion_products')
        .select('promotion_id,product_id')
        .order('created_at', { ascending: false }),
    ]);

    if (promotionsError) {
      console.error(promotionsError);
    }

    if (promotionProductsError) {
      console.error(promotionProductsError);
    }

    const normalizedPromotions = (promotionsData || [])
      .map((promotion: any) => {
        const imageSrc = normalizeImageUrl(promotion.promotion);
        return promotion.id && imageSrc ? {
          id: String(promotion.id),
          name: String(promotion.name || 'Promocion'),
          imageSrc,
        } as Promotion : null;
      })
      .filter(Boolean) as Promotion[];

    const groupedPromotionProducts = (promotionProductsData || []).reduce((acc: Record<string, string[]>, item: any) => {
      const promotionId = String(item.promotion_id || '');
      const productId = String(item.product_id || '');
      if (!promotionId || !productId) return acc;
      if (!acc[promotionId]) acc[promotionId] = [];
      acc[promotionId].push(productId);
      return acc;
    }, {});

    const payload = {
      products: cachedCatalog?.products || [],
      promotions: normalizedPromotions,
      promotionProductMap: groupedPromotionProducts,
    };

    writeCatalogCache(payload);

    return {
      ...payload,
      savedAt: inMemoryCatalogCache?.savedAt || Date.now(),
    };
  })();

  try {
    return await catalogLoadPromise;
  } finally {
    catalogLoadPromise = null;
  }
}

function FilterDropdown({
  label,
  value,
  placeholder,
  options,
  isOpen,
  onToggle,
  onClose,
  onSelect,
}: FilterDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        onClose();
      }
    };

    window.addEventListener('mousedown', handlePointerDown);
    return () => window.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen, onClose]);

  return (
    <div ref={dropdownRef} className="relative">
      <label className="mb-2 block text-sm font-medium text-slate-900">{label}</label>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between rounded-2xl border border-white/50 bg-white/60 px-4 py-3 text-left text-slate-900 outline-none backdrop-blur-md"
        aria-expanded={isOpen}
      >
        <span className={value ? 'text-slate-900' : 'text-slate-600'}>{value || placeholder}</span>
        <span className={"text-sm transition-transform duration-200 " + (isOpen ? 'rotate-180' : '')}>▼</span>
      </button>
      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-2xl border border-white/60 bg-white/80 shadow-xl backdrop-blur-md">
          <div className="max-h-56 overflow-y-auto p-2">
            <button
              type="button"
              onClick={() => onSelect('')}
              className={"mb-1 w-full rounded-xl px-3 py-2 text-left text-sm transition-colors duration-150 " + (!value ? 'bg-[#B2D4FF]/60 text-slate-900' : 'text-slate-700 hover:bg-white/70')}
            >
              {placeholder}
            </button>
            {options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => onSelect(option)}
                className={"block w-full rounded-xl px-3 py-2 text-left text-sm transition-colors duration-150 " + (value === option ? 'bg-[#FFC7F8]/60 text-slate-900' : 'text-slate-700 hover:bg-white/70')}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const [selected, setSelected] = useState<Product | null>(null);
  const [selectedModalSize, setSelectedModalSize] = useState<string>('');
  const [selectedModalQuantity, setSelectedModalQuantity] = useState<number>(1);
  const [isSizePickerOpen, setIsSizePickerOpen] = useState<boolean>(false);
  const [isCartModalOpen, setIsCartModalOpen] = useState<boolean>(false);
  const [editingCartItem, setEditingCartItem] = useState<{ product: Product; previousSize: string } | null>(null);
  const [cartShareFeedback, setCartShareFeedback] = useState<string>('');
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const storedCart = window.localStorage.getItem(CART_STORAGE_KEY);
      return storedCart ? JSON.parse(storedCart) as CartItem[] : [];
    } catch {
      return [];
    }
  });
  const [page, setPage] = useState<number>(1);
  const pageSize = 8;
  const [products, setProducts] = useState<Product[]>([]);
  const [paginatedProducts, setPaginatedProducts] = useState<Product[]>([]);
  const [matchingProductIds, setMatchingProductIds] = useState<string[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [promotionProductMap, setPromotionProductMap] = useState<Record<string, string[]>>({});
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [activeSection, setActiveSection] = useState<'recommended' | 'products'>('recommended');
  const [activePromoId, setActivePromoId] = useState<string>('');
  const [activePromoImageSrc, setActivePromoImageSrc] = useState<string>('');
  const [visiblePromoImageSrc, setVisiblePromoImageSrc] = useState<string>('');
  const [isProductsVisible, setIsProductsVisible] = useState<boolean>(true);
  const [isProductsLoading, setIsProductsLoading] = useState<boolean>(false);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState<boolean>(false);
  const [openFilterDropdown, setOpenFilterDropdown] = useState<string>('');
  const [selectedGenderFilter, setSelectedGenderFilter] = useState<string>('');
  const [selectedSizeFilter, setSelectedSizeFilter] = useState<string>('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('');
  const [selectedBrandFilter, setSelectedBrandFilter] = useState<string>('');
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [genderOptions, setGenderOptions] = useState<string[]>([]);
  const [sizeOptions, setSizeOptions] = useState<string[]>([]);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [promoPalettes, setPromoPalettes] = useState<Record<string, [number, number, number][]>>({});
  const [paginationBottomOffset, setPaginationBottomOffset] = useState<number>(16);
  const promoTransitionTimeout = useRef<number | null>(null);
  const processedSharedCartRef = useRef<string>('');
  const productsRef = useRef<Product[]>([]);
  const promotionsRef = useRef<Promotion[]>([]);
  const promotionProductMapRef = useRef<Record<string, string[]>>({});
  const previousPageRef = useRef<number>(1);
  const footerRef = useRef<HTMLElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);
  const productModalContentRef = useRef<HTMLDivElement | null>(null);
  const relatedCarouselRef = useRef<HTMLDivElement | null>(null);

  const currency = useMemo(() => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }), []);

  const persistCatalogState = useCallback((nextProducts: Product[], nextPromotions: Promotion[] = promotionsRef.current, nextPromotionProductMap: Record<string, string[]> = promotionProductMapRef.current) => {
    writeCatalogCache({
      products: nextProducts,
      promotions: nextPromotions,
      promotionProductMap: nextPromotionProductMap,
    });
  }, []);

  const mergeFetchedProducts = useCallback((nextProducts: Product[]) => {
    const mergedProducts = mergeProductsById(productsRef.current, nextProducts);
    productsRef.current = mergedProducts;
    setProducts(mergedProducts);
    persistCatalogState(mergedProducts);
    return mergedProducts;
  }, [persistCatalogState]);

  const ensureProductsLoaded = useCallback(async (productIds: string[]) => {
    const normalizedProductIds = Array.from(new Set(productIds.filter(Boolean)));
    const missingProductIds = normalizedProductIds.filter((productId) => !productsRef.current.some((product) => product.id === productId));

    if (missingProductIds.length === 0) return productsRef.current;

    const fetchedProducts = await fetchProductsByIds(missingProductIds);
    if (fetchedProducts.length === 0) return productsRef.current;

    return mergeFetchedProducts(fetchedProducts);
  }, [mergeFetchedProducts]);

  const currentProductQueryFilters = useMemo<ProductQueryFilters>(() => {
    if (activeSection !== 'products') {
      return activePromoId ? { productIds: promotionProductMap[activePromoId] || [] } : {};
    }

    return {
      category: selectedCategoryFilter || undefined,
      gender: selectedGenderFilter || undefined,
      brand: selectedBrandFilter || undefined,
      size: selectedSizeFilter || undefined,
    };
  }, [activePromoId, activeSection, promotionProductMap, selectedBrandFilter, selectedCategoryFilter, selectedGenderFilter, selectedSizeFilter]);
  const currentProductQueryCacheKey = useMemo(() => buildProductQueryCacheKey(currentProductQueryFilters), [currentProductQueryFilters]);

  const ensureBarcodesForCartItems = async (items: CartItem[]) => {
    const missingProductIds = Array.from(new Set(
      items
        .filter((item) => {
          const product = products.find((productItem) => productItem.id === item.productId);
          return product && !String(product.sizeBarcodes[item.size] || '').trim();
        })
        .map((item) => item.productId),
    ));

    if (missingProductIds.length === 0) return products;

    const barcodeMap = await fetchBarcodeMapForProductIds(missingProductIds);
    if (Object.keys(barcodeMap).length === 0) return products;

    const mergedProducts = mergeBarcodeMapIntoProducts(products, barcodeMap);
    setProducts((current) => mergeBarcodeMapIntoProducts(current, barcodeMap));
    persistCatalogState(mergedProducts);
    return mergedProducts;
  };

  const prepareCartShareData = async () => {
    const preparedProducts = await ensureBarcodesForCartItems(cartItems);
    const shareEntries = buildCartShareEntries(cartItems, preparedProducts);
    return {
      shareEntries,
      shareUrl: buildCartShareUrl(shareEntries),
    };
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelected(null);
        setIsSizePickerOpen(false);
        setIsFiltersModalOpen(false);
        setIsCartModalOpen(false);
        setEditingCartItem(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (editingCartItem) {
      setSelectedModalSize(editingCartItem.previousSize);
      setSelectedModalQuantity(1);
      setIsSizePickerOpen(true);
      return;
    }

    if (!selected) {
      setSelectedModalSize('');
      setSelectedModalQuantity(1);
      setIsSizePickerOpen(false);
      return;
    }

    if (selected.sizes.length === 1) {
      setSelectedModalSize(selected.sizes[0]);
      setSelectedModalQuantity(1);
      return;
    }

    setSelectedModalSize('');
    setSelectedModalQuantity(1);
    setIsSizePickerOpen(false);
  }, [selected, editingCartItem]);

  useEffect(() => {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    if (editingCartItem || !selected || !selectedModalSize) return;
    const totalStock = selected.sizeStock[selectedModalSize] || 0;
    const reservedQuantity = cartItems.reduce((total, item) => {
      if (item.productId !== selected.id || item.size !== selectedModalSize) return total;
      return total + item.quantity;
    }, 0);
    const availableStock = Math.max(0, totalStock - reservedQuantity);
    setSelectedModalQuantity((current) => {
      if (availableStock <= 0) return 1;
      return Math.min(current, availableStock);
    });
  }, [cartItems, editingCartItem, selected, selectedModalSize]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      const catalogData = await loadCatalogData();
      if (!isMounted) return;
      productsRef.current = catalogData.products;
      promotionsRef.current = catalogData.promotions;
      promotionProductMapRef.current = catalogData.promotionProductMap;
      setProducts(catalogData.products);
      setPromotions(catalogData.promotions);
      setPromotionProductMap(catalogData.promotionProductMap);
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  useEffect(() => {
    promotionsRef.current = promotions;
  }, [promotions]);

  useEffect(() => {
    promotionProductMapRef.current = promotionProductMap;
  }, [promotionProductMap]);

  useEffect(() => {
    if (activeSection !== 'products') {
      setCategoryOptions([]);
      setGenderOptions([]);
      setSizeOptions([]);
      setBrandOptions([]);
      return;
    }

    let isCancelled = false;

    (async () => {
      const [nextCategoryOptions, nextGenderOptions, nextSizeOptions, nextBrandOptions] = await Promise.all([
        fetchProductFieldValues('category', {
          gender: selectedGenderFilter || undefined,
          brand: selectedBrandFilter || undefined,
          size: selectedSizeFilter || undefined,
        }),
        fetchProductFieldValues('gender', {
          category: selectedCategoryFilter || undefined,
          brand: selectedBrandFilter || undefined,
          size: selectedSizeFilter || undefined,
        }),
        fetchAvailableSizes({
          category: selectedCategoryFilter || undefined,
          gender: selectedGenderFilter || undefined,
          brand: selectedBrandFilter || undefined,
        }),
        fetchProductFieldValues('brand', {
          category: selectedCategoryFilter || undefined,
          gender: selectedGenderFilter || undefined,
          size: selectedSizeFilter || undefined,
        }),
      ]);

      if (isCancelled) return;

      setCategoryOptions(nextCategoryOptions);
      setGenderOptions(nextGenderOptions);
      setSizeOptions(nextSizeOptions);
      setBrandOptions(nextBrandOptions);
    })();

    return () => {
      isCancelled = true;
    };
  }, [activeSection, selectedBrandFilter, selectedCategoryFilter, selectedGenderFilter, selectedSizeFilter]);

  useEffect(() => {
    if (!(activeSection === 'products' || activePromoId)) {
      setMatchingProductIds([]);
      setPaginatedProducts([]);
      setIsProductsLoading(false);
      return;
    }

    let isCancelled = false;

    (async () => {
      setIsProductsLoading(true);
      const ids = await fetchMatchingProductIds(currentProductQueryFilters, currentProductQueryCacheKey);
      if (isCancelled) return;

      setMatchingProductIds(ids);
      setIsProductsLoading(false);
    })();

    return () => {
      isCancelled = true;
    };
  }, [activePromoId, activeSection, currentProductQueryCacheKey, currentProductQueryFilters]);

  useEffect(() => {
    if (!(activeSection === 'products' || activePromoId)) {
      setPaginatedProducts([]);
      setIsProductsLoading(false);
      return;
    }

    let isCancelled = false;

    (async () => {
      setIsProductsLoading(true);

      const nextTotalPages = Math.max(1, Math.ceil(matchingProductIds.length / pageSize));
      const safePage = Math.min(page, nextTotalPages);
      if (safePage !== page) {
        setPage(safePage);
        return;
      }

      const startIndex = (safePage - 1) * pageSize;
      const collectedProducts: Product[] = [];
      const seenProductIds = new Set<string>();
      let cursor = startIndex;

      while (collectedProducts.length < pageSize && cursor < matchingProductIds.length) {
        const batchIds = matchingProductIds.slice(cursor, cursor + (pageSize - collectedProducts.length));
        if (batchIds.length === 0) break;

        const batchProducts = await fetchProductsByIds(batchIds);
        if (isCancelled) return;

        batchProducts.forEach((product) => {
          if (!seenProductIds.has(product.id)) {
            seenProductIds.add(product.id);
            collectedProducts.push(product);
          }
        });

        cursor += batchIds.length;
      }

      setPaginatedProducts(collectedProducts);
      if (collectedProducts.length > 0) {
        mergeFetchedProducts(collectedProducts);
      }
      setIsProductsLoading(false);
    })();

    return () => {
      isCancelled = true;
    };
  }, [activePromoId, activeSection, matchingProductIds, mergeFetchedProducts, page]);

  const totalPages = Math.max(1, Math.ceil(matchingProductIds.length / pageSize));
  const paginated = paginatedProducts;

  useEffect(() => {
    if (cartItems.length === 0) return;
    void ensureProductsLoaded(cartItems.map((item) => item.productId));
  }, [cartItems, ensureProductsLoaded]);

  useEffect(() => {
    if (!selected) {
      setRelatedProducts([]);
      return;
    }

    let isCancelled = false;

    (async () => {
      const nextRelatedProducts = await fetchRelatedProductsForProduct(selected);
      if (isCancelled) return;

      setRelatedProducts(nextRelatedProducts);
      if (nextRelatedProducts.length > 0) {
        mergeFetchedProducts(nextRelatedProducts);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [mergeFetchedProducts, selected]);

  const brandLogos = useMemo(() => ([
    { src: '/brands/adidas.svg', alt: 'Adidas' },
    { src: '/brands/CK.jpg', alt: 'Calvin Klein' },
    { src: '/brands/DKNY.png', alt: 'DKNY' },
    { src: '/brands/GUESS.png', alt: 'Guess' },
    { src: '/brands/MK.png', alt: 'Michael Kors' },
  ]), []);
  const isPromoActive = activePromoId !== '';
  const basePalette = useMemo<[number, number, number][]>(() => [
    [83, 213, 187],
    [110, 223, 201],
    [20, 44, 39],
  ], []);
  const currentPromoPalette = useMemo(() => {
    if (!visiblePromoImageSrc) return basePalette;
    return promoPalettes[visiblePromoImageSrc] || basePalette;
  }, [visiblePromoImageSrc, promoPalettes, basePalette]);
  const promoHeroes = useMemo(() => promotions, [promotions]);
  const shouldShowProductsGrid = activeSection === 'products' || isPromoActive;
  const cartItemsCount = useMemo(() => cartItems.reduce((total, item) => total + item.quantity, 0), [cartItems]);
  const cartTotal = useMemo(() => cartItems.reduce((total, item) => total + (item.price * item.quantity), 0), [cartItems]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    const previousPage = previousPageRef.current;
    previousPageRef.current = page;

    if (previousPage === page) return;
    if (!(activeSection === 'products' || activePromoId)) return;
    if (!mainRef.current) return;

    const top = Math.max(0, mainRef.current.offsetTop - 24);
    animateWindowScroll(top, 650);
  }, [activePromoId, activeSection, page]);

  useEffect(() => {
    if (!relatedCarouselRef.current) return;
    relatedCarouselRef.current.scrollTo({ left: 0, behavior: 'auto' });
  }, [selected]);

  useEffect(() => {
    if (!productModalContentRef.current) return;
    productModalContentRef.current.scrollTo({ top: 0, behavior: 'auto' });
  }, [selected]);

  useEffect(() => {
    if (activePromoImageSrc) {
      setVisiblePromoImageSrc(activePromoImageSrc);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setVisiblePromoImageSrc('');
    }, 700);

    return () => window.clearTimeout(timeoutId);
  }, [activePromoImageSrc]);

  useEffect(() => {
    promoHeroes.forEach((promo) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = promo.imageSrc;
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return;
        canvas.width = 48;
        canvas.height = 48;
        context.drawImage(image, 0, 0, 48, 48);
        try {
          const imageData = context.getImageData(0, 0, 48, 48);
          setPromoPalettes((current) => ({ ...current, [promo.imageSrc]: extractPalette(imageData.data) }));
        } catch (error) {
          console.warn('No se pudo extraer la paleta de la promocion', promo.imageSrc, error);
        }
      };
    });
  }, [promoHeroes]);

  useEffect(() => {
    const updatePaginationBottomOffset = () => {
      if (!footerRef.current) return;
      const footerRect = footerRef.current.getBoundingClientRect();
      const baseOffset = 16;
      const softZone = 180;
      const distanceToViewportBottom = footerRect.top - window.innerHeight;

      if (distanceToViewportBottom >= softZone) {
        setPaginationBottomOffset(baseOffset);
        return;
      }

      if (distanceToViewportBottom > 0) {
        const progress = (softZone - distanceToViewportBottom) / softZone;
        const easedOffset = baseOffset + progress * progress * 52;
        setPaginationBottomOffset(easedOffset);
        return;
      }

      setPaginationBottomOffset(baseOffset + 52 + Math.abs(distanceToViewportBottom));
    };

    updatePaginationBottomOffset();
    window.addEventListener('scroll', updatePaginationBottomOffset, { passive: true });
    window.addEventListener('resize', updatePaginationBottomOffset);

    return () => {
      window.removeEventListener('scroll', updatePaginationBottomOffset);
      window.removeEventListener('resize', updatePaginationBottomOffset);
    };
  }, []);

  useEffect(() => {
    if (activeSection === 'products') {
      setPage(1);
    }
  }, [activeSection, selectedCategoryFilter, selectedGenderFilter, selectedSizeFilter, selectedBrandFilter]);

  useEffect(() => {
    if (selectedCategoryFilter && !categoryOptions.includes(selectedCategoryFilter)) {
      setSelectedCategoryFilter('');
    }
  }, [selectedCategoryFilter, categoryOptions]);

  useEffect(() => {
    if (selectedGenderFilter && !genderOptions.includes(selectedGenderFilter)) {
      setSelectedGenderFilter('');
    }
  }, [selectedGenderFilter, genderOptions]);

  useEffect(() => {
    if (selectedSizeFilter && !sizeOptions.includes(selectedSizeFilter)) {
      setSelectedSizeFilter('');
    }
  }, [selectedSizeFilter, sizeOptions]);

  useEffect(() => {
    if (selectedBrandFilter && !brandOptions.includes(selectedBrandFilter)) {
      setSelectedBrandFilter('');
    }
  }, [selectedBrandFilter, brandOptions]);

  useEffect(() => {
    if (!isFiltersModalOpen) {
      setOpenFilterDropdown('');
    }
  }, [isFiltersModalOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const sharedCartValue = new URLSearchParams(window.location.search).get(SHARED_CART_QUERY_PARAM) || '';
    if (!sharedCartValue || processedSharedCartRef.current === sharedCartValue) return;

    let isCancelled = false;

    (async () => {
      try {
        const parsedEntries = JSON.parse(sharedCartValue) as Array<{ code?: string; quantity?: number }>;
        if (!Array.isArray(parsedEntries)) {
          processedSharedCartRef.current = sharedCartValue;
          return;
        }

        const numericCodes = Array.from(new Set(
          parsedEntries
            .map((entry) => String(entry?.code || '').trim())
            .filter((code) => /^\d+$/.test(code)),
        ));
        const sizeIdCodes = Array.from(new Set(
          parsedEntries
            .map((entry) => String(entry?.code || '').trim())
            .filter((code) => code && !/^\d+$/.test(code)),
        ));

        const [barcodeEntries, sizeEntries] = await Promise.all([
          fetchBarcodeEntriesByCodes(numericCodes),
          fetchProductSizeEntriesByIds(sizeIdCodes),
        ]);

        if (isCancelled) return;

        const neededProductIds = Array.from(new Set([
          ...Object.values(barcodeEntries).map((entry) => entry.productId),
          ...Object.values(sizeEntries).map((entry) => entry.productId),
        ]));

        let resolvedProducts = await ensureProductsLoaded(neededProductIds);
        if (isCancelled) return;

        const barcodeMap = Object.entries(barcodeEntries).reduce((acc: Record<string, string>, [barcode, entry]) => {
          acc[`${entry.productId}::${entry.size}`] = barcode;
          return acc;
        }, {});

        if (Object.keys(barcodeMap).length > 0) {
          resolvedProducts = mergeBarcodeMapIntoProducts(resolvedProducts, barcodeMap);
          setProducts((current) => mergeBarcodeMapIntoProducts(current, barcodeMap));
          persistCatalogState(resolvedProducts);
        }

        const resolvedItems = parsedEntries.reduce((acc: CartItem[], entry) => {
          const code = String(entry?.code || '').trim();
          const quantity = Math.max(1, Number(entry?.quantity || 1));
          if (!code) return acc;

          const resolvedEntry = barcodeEntries[code] || sizeEntries[code];
          let product = resolvedEntry
            ? resolvedProducts.find((item) => item.id === resolvedEntry.productId)
            : resolvedProducts.find((item) => item.sizes.some((size) => item.sizeBarcodes[size] === code || item.sizeIds[size] === code));

          let size = resolvedEntry?.size || '';

          if (!size && product) {
            const matchedProduct = product;
            size = matchedProduct.sizes.find((itemSize) => matchedProduct.sizeBarcodes[itemSize] === code || matchedProduct.sizeIds[itemSize] === code) || '';
          }

          if (!product || !size) return acc;

          const matchedProduct = product;
          const availableStock = matchedProduct.sizeStock[size] || 0;
          if (availableStock <= 0) return acc;

          const normalizedQuantity = Math.min(quantity, availableStock);
          const existingItem = acc.find((item) => item.productId === matchedProduct.id && item.size === size);
          if (existingItem) {
            existingItem.quantity = Math.min(existingItem.quantity + normalizedQuantity, availableStock);
            return acc;
          }

          acc.push({
            productId: matchedProduct.id,
            name: matchedProduct.name,
            price: matchedProduct.price,
            imageUrl: matchedProduct.imageUrl,
            size,
            quantity: normalizedQuantity,
          });
          return acc;
        }, []);

        if (resolvedItems.length > 0) {
          setCartItems(resolvedItems);
          setSelected(null);
          setEditingCartItem(null);
          setIsSizePickerOpen(false);
          setIsCartModalOpen(true);
        }

        processedSharedCartRef.current = sharedCartValue;
      } catch {
        processedSharedCartRef.current = sharedCartValue;
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [ensureProductsLoaded, persistCatalogState]);

  useEffect(() => {
    if (!cartShareFeedback) return;
    const timeoutId = window.setTimeout(() => setCartShareFeedback(''), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [cartShareFeedback]);

  useEffect(() => {
    return () => {
      if (promoTransitionTimeout.current) {
        window.clearTimeout(promoTransitionTimeout.current);
      }
    };
  }, []);

  const handlePromoHeroClick = (promotionId: string, imageSrc: string) => {
    if (promoTransitionTimeout.current) {
      window.clearTimeout(promoTransitionTimeout.current);
    }

    setActiveSection('recommended');
    const isOpeningPromo = activePromoId !== promotionId;

    setIsProductsVisible(false);

    promoTransitionTimeout.current = window.setTimeout(() => {
      setActivePromoId((currentPromotionId) => currentPromotionId === promotionId ? '' : promotionId);
      setActivePromoImageSrc((currentImageSrc) => currentImageSrc === imageSrc ? '' : imageSrc);
      setPage(1);
      setIsProductsVisible(true);

      if (isOpeningPromo && mainRef.current) {
        const top = Math.max(0, mainRef.current.offsetTop - 24);
        animateWindowScroll(top, 900);
      }
    }, 220);
  };

  const handleSectionChange = (section: 'recommended' | 'products') => {
    setActiveSection(section);
    setPage(1);
    setIsFiltersModalOpen(false);
    setOpenFilterDropdown('');
    if (section === 'products') {
      setActivePromoId('');
      setActivePromoImageSrc('');
      setIsProductsVisible(true);
    }
  };

  const handleClearFilters = () => {
    setSelectedGenderFilter('');
    setSelectedSizeFilter('');
    setSelectedCategoryFilter('');
    setSelectedBrandFilter('');
    setOpenFilterDropdown('');
  };

  const getCartQuantityForSize = (productId: string, size: string, excludedItem?: { productId: string; size: string }) => (
    cartItems.reduce((total, item) => {
      const isExcluded = excludedItem && item.productId === excludedItem.productId && item.size === excludedItem.size;
      if (isExcluded) return total;
      if (item.productId !== productId || item.size !== size) return total;
      return total + item.quantity;
    }, 0)
  );

  const getAvailableStockForSize = (product: Product, size: string, excludedItem?: { productId: string; size: string }) => {
    const totalStock = product.sizeStock[size] || 0;
    const reservedQuantity = getCartQuantityForSize(product.id, size, excludedItem);
    return Math.max(0, totalStock - reservedQuantity);
  };

  const handleAddToCart = (product: Product, size: string, quantity: number = 1) => {
    const maxAvailableStock = getAvailableStockForSize(product, size);
    if (maxAvailableStock <= 0) return;
    const normalizedQuantity = Math.min(Math.max(1, quantity), maxAvailableStock);

    setCartItems((currentItems) => {
      const existingItemIndex = currentItems.findIndex((item) => item.productId === product.id && item.size === size);

      if (existingItemIndex >= 0) {
        return currentItems.map((item, index) => (
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + normalizedQuantity }
            : item
        ));
      }

      return [
        ...currentItems,
        {
          productId: product.id,
          name: product.name,
          price: product.price,
          imageUrl: product.imageUrl,
          size,
          quantity: normalizedQuantity,
        },
      ];
    });

    setIsSizePickerOpen(false);
    setSelectedModalSize('');
    setSelectedModalQuantity(1);
    setEditingCartItem(null);
    setSelected(null);
  };

  const handleAddSelectedProduct = () => {
    if (!selected) return;
    setSelectedModalSize(selected.sizes.length === 1 ? selected.sizes[0] : '');
    setSelectedModalQuantity(1);
    setIsSizePickerOpen(true);
  };

  const handleShareCart = async () => {
    if (cartItems.length === 0) return;

    const { shareUrl } = await prepareCartShareData();
    if (!shareUrl) return;

    let copiedToClipboard = false;
    try {
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(shareUrl);
          copiedToClipboard = true;
        } catch {
          copiedToClipboard = false;
        }
      }

      const shareNavigator = navigator as Navigator & { share?: (data: { title?: string; text?: string }) => Promise<void> };
      if (typeof shareNavigator.share === 'function') {
        await shareNavigator.share({ title: 'Mi carrito', text: shareUrl });
        setCartShareFeedback(copiedToClipboard ? 'Link compartido y copiado' : 'Link compartido');
        return;
      }

      if (copiedToClipboard) {
        setCartShareFeedback('Link copiado');
        return;
      }

      setCartShareFeedback('No se pudo compartir');
    } catch (error) {
      if ((error as { name?: string }).name === 'AbortError') {
        setCartShareFeedback(copiedToClipboard ? 'Link copiado' : '');
        return;
      }
      setCartShareFeedback('No se pudo compartir');
    }
  };

  const handleCopyCartJson = async () => {
    if (!navigator.clipboard?.writeText) return;

    const { shareEntries } = await prepareCartShareData();
    if (shareEntries.length === 0) return;

    try {
      await navigator.clipboard.writeText(JSON.stringify(shareEntries, null, 2));
      setCartShareFeedback('JSON copiado');
    } catch {
      setCartShareFeedback('No se pudo copiar el JSON');
    }
  };

  const handleClearCart = () => {
    setCartItems([]);
    setCartShareFeedback('');
  };

  const handleCloseSizePicker = () => {
    setIsSizePickerOpen(false);
    setSelectedModalSize('');
    setSelectedModalQuantity(1);
    setEditingCartItem(null);
  };

  const handleChangeCartItemQuantity = async (productId: string, size: string, delta: number) => {
    const resolvedProducts = await ensureProductsLoaded([productId]);
    const product = resolvedProducts.find((item) => item.id === productId);
    setCartItems((currentItems) => currentItems.flatMap((item) => {
      if (item.productId !== productId || item.size !== size) return [item];
      if (delta > 0 && product && item.quantity >= (product.sizeStock[size] || 0)) return [item];
      const nextQuantity = item.quantity + delta;
      if (nextQuantity <= 0) return [];
      return [{ ...item, quantity: nextQuantity }];
    }));
  };

  const handleStartCartItemEdit = async (productId: string, size: string) => {
    const resolvedProducts = await ensureProductsLoaded([productId]);
    const product = resolvedProducts.find((item) => item.id === productId);
    if (!product) return;
    setEditingCartItem({ product, previousSize: size });
  };

  const handleUpdateCartItemSize = async (productId: string, previousSize: string, nextSize: string) => {
    if (previousSize === nextSize) {
      handleCloseSizePicker();
      return;
    }

    const resolvedProducts = await ensureProductsLoaded([productId]);
    const resolvedProduct = resolvedProducts.find((item) => item.id === productId);
    if (!resolvedProduct) return;

    setCartItems((currentItems) => {
      const sourceItem = currentItems.find((item) => item.productId === productId && item.size === previousSize);
      if (!sourceItem) return currentItems;

      const availableTargetStock = getAvailableStockForSize(resolvedProduct, nextSize);
      if (availableTargetStock < 1) return currentItems;

      const targetItem = currentItems.find((item) => item.productId === productId && item.size === nextSize);
      const itemsAfterSourceUpdate = currentItems.flatMap((item) => {
        if (item.productId !== productId || item.size !== previousSize) return [item];
        if (item.quantity <= 1) return [];
        return [{ ...item, quantity: item.quantity - 1 }];
      });

      if (targetItem) {
        return itemsAfterSourceUpdate
          .map((item) => (
            item.productId === productId && item.size === nextSize
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ));
      }

      return [
        ...itemsAfterSourceUpdate,
        {
          productId: sourceItem.productId,
          name: sourceItem.name,
          price: sourceItem.price,
          imageUrl: sourceItem.imageUrl,
          size: nextSize,
          quantity: 1,
        },
      ];
    });

    handleCloseSizePicker();
  };

  const handleConfirmSelectedSize = () => {
    if (!selectedModalSize) return;
    if (editingCartItem) {
      void handleUpdateCartItemSize(editingCartItem.product.id, editingCartItem.previousSize, selectedModalSize);
      return;
    }
    if (!selected) return;
    handleAddToCart(selected, selectedModalSize, selectedModalQuantity);
  };

  const handleOpenCartProduct = async (productId: string) => {
    const resolvedProducts = await ensureProductsLoaded([productId]);
    const product = resolvedProducts.find((item) => item.id === productId);
    if (!product) return;
    setIsCartModalOpen(false);
    setSelected(product);
  };

  const handleScrollRelatedProducts = (direction: 'prev' | 'next') => {
    if (!relatedCarouselRef.current) return;
    const amount = Math.max(220, relatedCarouselRef.current.clientWidth * 0.72);
    relatedCarouselRef.current.scrollBy({
      left: direction === 'next' ? amount : -amount,
      behavior: 'smooth',
    });
  };

  const sizePickerProduct = editingCartItem ? editingCartItem.product : selected;
  const selectedSizeRemainingStock = !sizePickerProduct || !selectedModalSize
    ? 0
    : editingCartItem
      ? selectedModalSize === editingCartItem.previousSize
        ? (sizePickerProduct.sizeStock[selectedModalSize] || 0)
        : getAvailableStockForSize(sizePickerProduct, selectedModalSize, { productId: editingCartItem.product.id, size: editingCartItem.previousSize })
      : getAvailableStockForSize(sizePickerProduct, selectedModalSize);
  const pickerQuantity = editingCartItem ? 1 : selectedModalQuantity;
  const canConfirmSelectedSize = editingCartItem
    ? Boolean(selectedModalSize) && selectedSizeRemainingStock > 0
    : Boolean(selectedModalSize) && selectedSizeRemainingStock > 0 && selectedModalQuantity <= selectedSizeRemainingStock;

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div
        className="pointer-events-none fixed inset-0 z-0 animate-gradient"
        style={{ backgroundImage: 'linear-gradient(135deg, #53D5BB 0%, #6EDFC9 48%, #142C27 100%)' }}
      />
      <div
        className={"pointer-events-none fixed inset-0 z-[1] transition-opacity duration-700 ease-in-out " + (isPromoActive ? 'opacity-100' : 'opacity-0')}
        style={{
          backgroundImage: `linear-gradient(135deg, ${toRgba(currentPromoPalette[0], 0.95)} 0%, ${toRgba(currentPromoPalette[1], 0.92)} 52%, ${toRgba(currentPromoPalette[2], 0.9)} 100%)`,
        }}
      />
      <div className="relative z-10 min-h-screen bg-black/5">
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/30 backdrop-blur-md border-b border-white/50 overflow-hidden">
          <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex-1"></div>
            <button type="button" onClick={() => window.location.reload()} className="inline-flex items-center justify-center" aria-label="Recargar pagina">
              <img src="/LOGO.png" alt="Fashion & Style" className="h-14 sm:h-24 max-w-full object-contain" />
            </button>
            <div className="flex-1"></div>
          </div>
          <div className="w-full overflow-hidden border-t border-white/40 bg-white/35">
            <div className="marquee-track py-1.5">
              {[0, 1].map(group => (
                <div key={group} className="marquee-group" aria-hidden={group === 1}>
                  {brandLogos.map((logo, index) => (
                    <div key={`${group}-${index}`} className="flex h-8 min-w-0 items-center justify-center overflow-hidden rounded-xl bg-white/80 px-2 shadow-md ring-1 ring-white/60">
                      <img src={logo.src} alt={logo.alt} className="h-full w-full object-contain p-1" draggable={false} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </header>

        <main ref={mainRef} className="max-w-7xl mx-auto px-6 pb-16 pt-40 sm:pt-48">
          {!supabase && (
            <div className="mb-4 rounded-lg bg-red-100 text-red-700 px-4 py-2">
              Configuración de Supabase ausente. Agrega REACT_APP_SUPABASE_URL y REACT_APP_SUPABASE_ANON_KEY.
            </div>
          )}
          <div className="mb-6 flex justify-center">
            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-full border border-white/60 bg-white/60 p-1 shadow-lg backdrop-blur-md">
                <button
                  type="button"
                  onClick={() => handleSectionChange('recommended')}
                  className={"rounded-full px-5 py-2 text-sm font-medium transition-colors duration-200 " + (activeSection === 'recommended' ? 'bg-[#FFC7F8] text-slate-900' : 'bg-transparent text-slate-700')}
                >
                  Recomendados
                </button>
                <button
                  type="button"
                  onClick={() => handleSectionChange('products')}
                  className={"rounded-full px-5 py-2 text-sm font-medium transition-colors duration-200 " + (activeSection === 'products' ? 'bg-[#B2D4FF] text-slate-900' : 'bg-transparent text-slate-700')}
                >
                  Productos
                </button>
              </div>
              <button
                type="button"
                onClick={() => setIsCartModalOpen(true)}
                className="relative inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/60 bg-white/60 text-slate-900 shadow-lg backdrop-blur-md transition-all duration-200 hover:bg-white/75"
                aria-label="Abrir carrito"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h2.2c.4 0 .76.27.87.66L6.7 7H19a1 1 0 0 1 .97 1.25l-1.2 4.8a1 1 0 0 1-.97.75H8.1a1 1 0 0 1-.97-.76L5.1 5.5M9 19a1.25 1.25 0 1 0 0 2.5A1.25 1.25 0 0 0 9 19Zm8 0a1.25 1.25 0 1 0 0 2.5A1.25 1.25 0 0 0 17 19Z" />
                </svg>
                {cartItemsCount > 0 && (
                  <span className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold leading-none text-white shadow-md">
                    {cartItemsCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          {activeSection === 'products' && (
            <div className="mb-6 flex justify-center">
              <div className="flex items-center gap-3">
                {(selectedCategoryFilter || selectedGenderFilter || selectedSizeFilter || selectedBrandFilter) && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="inline-flex items-center gap-2 rounded-full border border-white/60 bg-white/65 px-5 py-2.5 text-sm font-medium text-slate-900 shadow-lg backdrop-blur-md transition-transform duration-200 hover:scale-[1.02]"
                  >
                    Limpiar filtros
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsFiltersModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-full border border-white/60 bg-white/65 px-5 py-2.5 text-sm font-medium text-slate-900 shadow-lg backdrop-blur-md transition-transform duration-200 hover:scale-[1.02]"
                >
                  <span>Filtros</span>
                  {(selectedCategoryFilter || selectedGenderFilter || selectedSizeFilter || selectedBrandFilter) && (
                    <span className="rounded-full bg-[#FFC7F8]/60 px-2 py-0.5 text-xs font-semibold text-slate-900">Activos</span>
                  )}
                </button>
              </div>
            </div>
          )}
          {activeSection === 'recommended' && (
            <>
              {promoHeroes.map((promo) => (
                (!isPromoActive || activePromoId === promo.id) && (
                  <button
                    key={promo.id}
                    type="button"
                    onClick={() => handlePromoHeroClick(promo.id, promo.imageSrc)}
                    className={"mb-6 block w-full overflow-hidden rounded-3xl border bg-white/30 shadow-xl backdrop-blur-md transition-all duration-300 " + (activePromoId === promo.id ? 'border-white/90 ring-2 ring-white/80 scale-[0.995]' : 'border-white/50 hover:scale-[1.01]')}
                  >
                    <img
                      src={promo.imageSrc}
                      alt={promo.name}
                      className="h-auto w-full object-cover"
                      loading="lazy"
                    />
                  </button>
                )
              ))}
            </>
          )}
          <section className="pb-24">
            {shouldShowProductsGrid && isProductsLoading ? (
              <div className="flex justify-center py-20">
                <div className="flex min-w-[220px] flex-col items-center gap-4 rounded-[2rem] border border-white/60 bg-[linear-gradient(145deg,rgba(255,255,255,0.72)_0%,rgba(255,243,248,0.62)_34%,rgba(240,252,249,0.56)_68%,rgba(255,255,255,0.7)_100%)] px-8 py-7 text-center text-slate-900 shadow-[0_22px_60px_rgba(15,42,38,0.12)] backdrop-blur-xl">
                  <div className="relative flex h-16 w-16 items-center justify-center">
                    <div className="absolute inset-0 rounded-full border border-white/70 bg-white/20" />
                    <div className="absolute inset-[6px] rounded-full border-4 border-[#53D5BB]/25 border-t-[#53D5BB] border-r-[#FFC7F8] animate-spin" />
                    <div className="absolute inset-[16px] rounded-full border-2 border-[#FFD66B]/25 border-b-[#FFD66B] animate-spin [animation-direction:reverse] [animation-duration:1.2s]" />
                    <div className="h-3 w-3 rounded-full bg-white/90 shadow-[0_0_18px_rgba(255,255,255,0.8)]" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-slate-900">Cargando productos</p>
                    <p className="text-xs text-slate-700">Estamos preparando esta ronda para ti</p>
                  </div>
                </div>
              </div>
            ) : shouldShowProductsGrid && matchingProductIds.length === 0 ? (
              <div className="text-center text-slate-900 py-20">Sin resultados</div>
            ) : shouldShowProductsGrid ? (
              <div className={"grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 transition-all duration-300 ease-out " + (isProductsVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0')}>
                {paginated.map(p => (
                  <article
                    key={p.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelected(p)}
                    className="cursor-pointer group rounded-2xl overflow-hidden bg-white/10 backdrop-blur-sm border border-white/10 shadow-lg focus:outline-none focus:ring-2 focus:ring-[#FFC7F8]"
                  >
                    <div className="relative h-48 lg:h-52">
                      <img
                        src={p.imageUrl}
                        alt={p.name}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                        className="h-full w-full object-contain rounded-xl bg-slate-900/20 p-2 ring-1 ring-white/10 backdrop-blur-sm"
                      />
                    </div>
                    <div className="border-t border-white/20 bg-[linear-gradient(145deg,rgba(255,255,255,0.62)_0%,rgba(255,244,248,0.52)_32%,rgba(244,255,253,0.44)_68%,rgba(255,255,255,0.58)_100%)] p-3 backdrop-blur-md">
                      <div className="flex flex-col items-start gap-1">
                        <h3 className="text-slate-900 text-sm lg:text-base font-semibold leading-tight">{p.name}</h3>
                        <span className="text-slate-900 text-sm lg:text-base font-bold">{currency.format(p.price)}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {p.sizes.map(s => (
                          <span
                            key={s}
                            className={"px-2 py-1 text-xs rounded-md " + (s === 'XS' ? 'bg-[#FFC7F8]/30 text-slate-900' : 'bg-white/50 text-slate-900')}
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                      {p.sizes.length === 1 && (
                        <div className="mt-2 inline-flex items-center rounded-full bg-gradient-to-r from-[#FF8ACF] via-[#FFC7F8] to-[#FFD66B] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-900 shadow-[0_10px_30px_rgba(255,138,207,0.35)] ring-1 ring-white/70">
                          Ultima talla
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="text-xs px-2 py-1 rounded bg-white/50 text-slate-900">{p.category}</span>
                        <span className="text-xs px-2 py-1 rounded bg-white/50 text-slate-900">{p.gender}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : null}
          </section>

          {selected && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-4" onClick={() => setSelected(null)}>
              <div ref={productModalContentRef} className="relative w-full max-w-4xl max-h-[calc(100vh-2rem)] overflow-y-auto rounded-3xl border border-white/60 bg-white/80 shadow-2xl backdrop-blur-xl" onClick={(e) => e.stopPropagation()}>
                <div className="pointer-events-none sticky top-3 z-20 h-0">
                  <div className="flex justify-end px-3">
                    <button aria-label="Cerrar" onClick={() => setSelected(null)} className="pointer-events-auto rounded-full bg-slate-900 text-white px-3 py-1 shadow-md hover:bg-slate-800">✕</button>
                  </div>
                </div>
                <div className="grid md:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]">
                  <div className="flex items-center justify-center rounded-t-3xl bg-white p-3 md:rounded-l-3xl md:rounded-tr-none">
                    <img src={selected.imageUrl} alt={selected.name} className="max-h-[34vh] w-full max-w-full object-contain md:max-h-[calc(100vh-16rem)]" />
                  </div>
                  <div className="bg-[linear-gradient(145deg,rgba(255,255,255,0.88)_0%,rgba(255,243,248,0.74)_32%,rgba(240,252,249,0.7)_68%,rgba(255,255,255,0.84)_100%)] p-6 text-slate-900 backdrop-blur-md">
                    <h2
                      className="text-2xl font-bold"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {selected.name}
                    </h2>
                    <p className="mt-2">{currency.format(selected.price)}</p>
                    <div className="mt-4 flex gap-2">
                        <span className="rounded-full border border-slate-300/90 bg-slate-200/90 px-2 py-1 text-xs shadow-[inset_0_1px_0_rgba(255,255,255,0.42)]">{selected.category}</span>
                        <span className="rounded-full border border-slate-300/90 bg-slate-200/90 px-2 py-1 text-xs shadow-[inset_0_1px_0_rgba(255,255,255,0.42)]">{selected.gender}</span>
                    </div>
                    <div className="mt-4">
                      <p className="text-sm text-slate-700 mb-2">Tallas disponibles</p>
                      <div className="flex flex-wrap gap-2">
                        {selected.sizes.map(s => (
                          <span key={s} className="rounded-full border border-slate-300/90 bg-slate-200/90 px-2.5 py-1 text-sm text-slate-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.42)]">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="mt-5">
                      <button
                        type="button"
                        onClick={handleAddSelectedProduct}
                        className="w-full rounded-full bg-gradient-to-r from-[#FF8ACF] via-[#FFC7F8] to-[#FFD66B] px-5 py-3 text-sm font-semibold text-slate-900 shadow-lg transition-transform duration-200 hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Agregar al carrito
                      </button>
                    </div>
                  </div>
                </div>
                <div className="border-t border-white/50 bg-white/35 px-5 py-5 sm:px-6">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-slate-900">Te podria interesar</h3>
                    {relatedProducts.length > 0 && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleScrollRelatedProducts('prev')}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/70 text-slate-900 shadow-sm transition-colors duration-200 hover:bg-white/90"
                          aria-label="Ver productos anteriores"
                        >
                          ‹
                        </button>
                        <button
                          type="button"
                          onClick={() => handleScrollRelatedProducts('next')}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/70 text-slate-900 shadow-sm transition-colors duration-200 hover:bg-white/90"
                          aria-label="Ver siguientes productos"
                        >
                          ›
                        </button>
                      </div>
                    )}
                  </div>
                  {relatedProducts.length > 0 ? (
                    <div
                      ref={relatedCarouselRef}
                      className="flex gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    >
                      {relatedProducts.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => setSelected(product)}
                          className="w-[150px] shrink-0 overflow-hidden rounded-2xl border border-white/75 bg-white text-left shadow-sm transition-transform duration-200 hover:scale-[1.01]"
                        >
                          <div className="flex h-36 items-center justify-center bg-white p-3">
                            <img src={product.imageUrl} alt={product.name} className="h-full w-full object-contain" />
                          </div>
                          <div className="p-3">
                            <p
                              className="text-sm font-semibold text-slate-900"
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {product.name}
                            </p>
                            <p className="mt-2 text-sm font-bold text-slate-900">{currency.format(product.price)}</p>
                            <p className="mt-1 truncate text-xs text-slate-700">{product.category}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-700">No encontramos mas sugerencias por ahora.</p>
                  )}
                </div>
              </div>
            </div>
          )}
          {(selected || editingCartItem) && isSizePickerOpen && (
            <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/45 px-4" onClick={handleCloseSizePicker}>
              <div
                className="w-full max-w-sm rounded-3xl border border-white/50 bg-white/85 p-6 shadow-2xl backdrop-blur-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold text-slate-900">Elige tu talla</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(editingCartItem ? editingCartItem.product.sizes : selected?.sizes || []).map((size) => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => {
                        setSelectedModalSize(size);
                        if (!editingCartItem) {
                          setSelectedModalQuantity(1);
                        }
                      }}
                      disabled={(editingCartItem
                        ? (size === editingCartItem.previousSize ? ((editingCartItem.product.sizeStock[size] || 0) > 0) : (getAvailableStockForSize(editingCartItem.product, size, { productId: editingCartItem.product.id, size: editingCartItem.previousSize }) > 0))
                        : Boolean(selected && getAvailableStockForSize(selected, size) > 0)) === false}
                      className={"rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-45 " + (selectedModalSize === size ? 'bg-slate-900 text-white' : 'bg-white/70 text-slate-900 ring-1 ring-white/70')}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                <div className="mt-5">
                  {!editingCartItem && (
                    <>
                      <p className="text-sm text-slate-700">Cantidad</p>
                      <div className="mt-2 inline-flex items-center rounded-full border border-white/60 bg-white/70 p-1 shadow-sm">
                        <button
                          type="button"
                          onClick={() => setSelectedModalQuantity((current) => Math.max(1, current - 1))}
                          disabled={!selectedModalSize || selectedModalQuantity <= 1}
                          className="inline-flex h-6 items-center justify-center rounded-full bg-rose-500 px-2.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-45"
                        >
                          -
                        </button>
                        <span className="min-w-8 px-2 text-center text-sm font-semibold text-slate-900">{pickerQuantity}</span>
                        <button
                          type="button"
                          onClick={() => setSelectedModalQuantity((current) => Math.min(selectedSizeRemainingStock, current + 1))}
                          disabled={!selectedModalSize || selectedSizeRemainingStock <= 0 || selectedModalQuantity >= selectedSizeRemainingStock}
                          className="inline-flex h-6 items-center justify-center rounded-full bg-emerald-500 px-2.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-45"
                        >
                          +
                        </button>
                      </div>
                      {selectedModalSize && (
                        <p className="mt-2 text-xs text-slate-700">Cantidad disponible: {selectedSizeRemainingStock}</p>
                      )}
                    </>
                  )}
                  {editingCartItem && selectedModalSize && selectedModalSize !== editingCartItem.previousSize && (
                    <p className="text-sm text-slate-700">
                      Se cambiara una talla, talla {editingCartItem.previousSize}, por talla {selectedModalSize}
                    </p>
                  )}
                </div>
                <div className="mt-5 flex gap-3">
                  <button
                    type="button"
                    onClick={handleCloseSizePicker}
                    className="flex-1 rounded-full border border-white/50 bg-white/60 px-4 py-2.5 text-sm font-medium text-slate-900"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmSelectedSize}
                    disabled={!canConfirmSelectedSize}
                    className="flex-1 rounded-full bg-gradient-to-r from-[#FF8ACF] via-[#FFC7F8] to-[#FFD66B] px-4 py-2.5 text-sm font-semibold text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {editingCartItem ? 'Guardar' : 'Agregar'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {isFiltersModalOpen && activeSection === 'products' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-4" onClick={() => setIsFiltersModalOpen(false)}>
              <div
                className="w-full max-w-lg rounded-3xl border border-white/40 bg-gradient-to-br from-white/30 to-white/10 p-6 shadow-2xl ring-1 ring-white/30 backdrop-blur-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-5 flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-slate-900">Filtros</h2>
                  <button
                    type="button"
                    onClick={() => setIsFiltersModalOpen(false)}
                    className="rounded-full bg-white/60 px-3 py-1 text-sm font-medium text-slate-900"
                  >
                    Cerrar
                  </button>
                </div>
                <div className="space-y-4">
                  <FilterDropdown
                    label="Marca"
                    value={selectedBrandFilter}
                    placeholder="Todas"
                    options={brandOptions}
                    isOpen={openFilterDropdown === 'brand'}
                    onToggle={() => setOpenFilterDropdown((current) => current === 'brand' ? '' : 'brand')}
                    onClose={() => setOpenFilterDropdown('')}
                    onSelect={(value) => {
                      setSelectedBrandFilter(value);
                      setOpenFilterDropdown('');
                    }}
                  />
                  <FilterDropdown
                    label="Genero"
                    value={selectedGenderFilter}
                    placeholder="Todos"
                    options={genderOptions}
                    isOpen={openFilterDropdown === 'gender'}
                    onToggle={() => setOpenFilterDropdown((current) => current === 'gender' ? '' : 'gender')}
                    onClose={() => setOpenFilterDropdown('')}
                    onSelect={(value) => {
                      setSelectedGenderFilter(value);
                      setOpenFilterDropdown('');
                    }}
                  />
                  <FilterDropdown
                    label="Talla"
                    value={selectedSizeFilter}
                    placeholder="Todas"
                    options={sizeOptions}
                    isOpen={openFilterDropdown === 'size'}
                    onToggle={() => setOpenFilterDropdown((current) => current === 'size' ? '' : 'size')}
                    onClose={() => setOpenFilterDropdown('')}
                    onSelect={(value) => {
                      setSelectedSizeFilter(value);
                      setOpenFilterDropdown('');
                    }}
                  />
                  <FilterDropdown
                    label="Categoria"
                    value={selectedCategoryFilter}
                    placeholder="Todas"
                    options={categoryOptions}
                    isOpen={openFilterDropdown === 'category'}
                    onToggle={() => setOpenFilterDropdown((current) => current === 'category' ? '' : 'category')}
                    onClose={() => setOpenFilterDropdown('')}
                    onSelect={(value) => {
                      setSelectedCategoryFilter(value);
                      setOpenFilterDropdown('');
                    }}
                  />
                </div>
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="rounded-full border border-white/50 bg-white/45 px-4 py-2 text-sm font-medium text-slate-900"
                  >
                    Limpiar filtros
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsFiltersModalOpen(false)}
                    className="rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          )}
          {isCartModalOpen && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4" onClick={() => setIsCartModalOpen(false)}>
              <div
                className="w-full max-w-md rounded-3xl border border-white/55 bg-[linear-gradient(145deg,rgba(255,255,255,0.86)_0%,rgba(255,243,248,0.72)_34%,rgba(240,252,249,0.68)_68%,rgba(255,255,255,0.82)_100%)] p-6 shadow-2xl ring-1 ring-white/35 backdrop-blur-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleCopyCartJson}
                      className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/65 bg-white/55 text-slate-900 shadow-md ring-1 ring-white/60"
                      aria-label="Copiar JSON tecnico del carrito"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h2.2c.4 0 .76.27.87.66L6.7 7H19a1 1 0 0 1 .97 1.25l-1.2 4.8a1 1 0 0 1-.97.75H8.1a1 1 0 0 1-.97-.76L5.1 5.5M9 19a1.25 1.25 0 1 0 0 2.5A1.25 1.25 0 0 0 9 19Zm8 0a1.25 1.25 0 1 0 0 2.5A1.25 1.25 0 0 0 17 19Z" />
                      </svg>
                    </button>
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900">Carrito</h2>
                      <p className="text-sm text-slate-700">{cartItemsCount === 0 ? 'Aun no agregas productos.' : `${cartItemsCount} articulo${cartItemsCount === 1 ? '' : 's'} en tu carrito.`}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsCartModalOpen(false)}
                    className="rounded-full border border-white/65 bg-white/55 px-3 py-1 text-sm font-medium text-slate-900"
                  >
                    Cerrar
                  </button>
                </div>
                <div className="mt-6 rounded-3xl border border-white/55 bg-[linear-gradient(145deg,rgba(255,255,255,0.72)_0%,rgba(255,243,248,0.58)_32%,rgba(240,252,249,0.5)_68%,rgba(255,255,255,0.68)_100%)] p-5 text-slate-800 shadow-inner shadow-white/20 backdrop-blur-md">
                  {cartItems.length === 0 ? (
                    <div>
                      <p className="text-base font-medium text-slate-900">Tu carrito esta vacio.</p>
                      <p className="mt-2 text-sm text-slate-700">Agrega productos al carrito para verlos aqui.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
                        {cartItems.map((item) => (
                          <div key={`${item.productId}-${item.size}`} className="rounded-2xl border border-[#C7D8EE]/90 bg-[linear-gradient(145deg,rgba(226,236,247,0.92)_0%,rgba(214,228,244,0.84)_34%,rgba(205,222,241,0.8)_68%,rgba(223,235,248,0.9)_100%)] p-4">
                            <p className="w-full truncate text-sm font-semibold text-slate-900">{item.name}</p>
                            <div className="mt-3 flex items-start gap-3">
                              <button
                                type="button"
                                onClick={() => handleOpenCartProduct(item.productId)}
                                className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-900/20 ring-1 ring-white/10 backdrop-blur-sm"
                              >
                                <img src={item.imageUrl} alt={item.name} className="h-full w-full object-contain p-2" />
                              </button>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs text-slate-700">Talla {item.size}</p>
                                <p className="mt-2 text-xs text-slate-700">Cantidad</p>
                                <div className="mt-1 inline-flex items-center rounded-full border border-white/60 bg-white/70 p-1 shadow-sm">
                                  <button
                                    type="button"
                                    onClick={() => handleChangeCartItemQuantity(item.productId, item.size, -1)}
                                    className="inline-flex h-6 items-center justify-center rounded-full bg-rose-500 px-2.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-rose-600"
                                    aria-label={`Disminuir cantidad de ${item.name}`}
                                  >
                                    -
                                  </button>
                                  <span className="min-w-8 px-2 text-center text-sm font-semibold text-slate-900">{item.quantity}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleChangeCartItemQuantity(item.productId, item.size, 1)}
                                    disabled={item.quantity >= ((products.find((productItem) => productItem.id === item.productId)?.sizeStock[item.size]) || 0)}
                                    className="inline-flex h-6 items-center justify-center rounded-full bg-emerald-500 px-2.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-45"
                                    aria-label={`Aumentar cantidad de ${item.name}`}
                                  >
                                    +
                                  </button>
                                </div>
                                <p className="mt-2 text-sm font-semibold text-slate-900">{currency.format(item.price * item.quantity)}</p>
                              </div>
                            </div>
                            {(() => {
                              const product = products.find((productItem) => productItem.id === item.productId);
                              const canModifySize = Boolean(product && product.sizes.length > 1);

                              return (
                                <div className="mt-4">
                                  {canModifySize ? (
                                    <button
                                      type="button"
                                      onClick={() => handleStartCartItemEdit(item.productId, item.size)}
                                      className="w-full rounded-full bg-white/70 px-4 py-2 text-sm font-medium text-slate-900 transition-colors duration-200 hover:bg-white/90"
                                    >
                                      Modificar
                                    </button>
                                  ) : (
                                    <div className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-[#FF8ACF] via-[#FFC7F8] to-[#FFD66B] px-4 py-2 text-center text-sm font-semibold text-slate-900 shadow-[0_10px_30px_rgba(255,138,207,0.35)] ring-1 ring-white/70">
                                      Talla Unica
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between rounded-2xl border border-[#C7D8EE]/90 bg-[#DCE9F8]/85 px-4 py-3">
                          <span className="text-sm font-medium text-slate-700">Total</span>
                          <span className="text-base font-bold text-slate-900">{currency.format(cartTotal)}</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleShareCart}
                          className="w-full rounded-full border border-[#4DBBA5]/90 bg-gradient-to-r from-[#53D5BB] via-[#67DCC5] to-[#48B7A2] px-4 py-3 text-sm font-semibold text-[#0F2A26] shadow-[0_12px_32px_rgba(83,213,187,0.28)] transition-all duration-200 hover:scale-[1.01] hover:brightness-[1.03]"
                        >
                          Compartir mi carrito
                        </button>
                        <button
                          type="button"
                          onClick={handleClearCart}
                          className="w-full rounded-full border border-[#C45A64]/90 bg-gradient-to-r from-[#D96B73] via-[#D0626C] to-[#B84F59] px-4 py-3 text-sm font-semibold text-white shadow-[0_12px_32px_rgba(184,79,89,0.24)] transition-all duration-200 hover:scale-[1.01] hover:brightness-[1.03]"
                        >
                          Vaciar mi carrito
                        </button>
                        {cartShareFeedback && (
                          <p className="text-center text-xs font-medium text-slate-700">{cartShareFeedback}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
        {shouldShowProductsGrid && matchingProductIds.length > 0 && (
          <div className="fixed left-1/2 z-40 -translate-x-1/2 transition-[bottom] duration-150 ease-out" style={{ bottom: `${paginationBottomOffset}px` }}>
            <div className="flex items-center gap-3 rounded-full border border-white/60 bg-white/75 px-3 py-2 shadow-xl backdrop-blur-md">
              <button disabled={page === 1 || isProductsLoading} onClick={() => setPage(p => Math.max(1, p - 1))} className="rounded-full bg-white/70 px-4 py-2 text-sm text-slate-900 disabled:opacity-50">Anterior</button>
              <button disabled={page === totalPages || isProductsLoading} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="rounded-full bg-white/70 px-4 py-2 text-sm text-slate-900 disabled:opacity-50">Siguiente</button>
            </div>
          </div>
        )}
        <footer ref={footerRef} className="border-t border-white/50 bg-white/40 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-6 py-8 text-slate-900">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="font-semibold">Contacto</p>
                <a href="https://wa.me/525572670701" className="inline-flex items-center gap-2 hover:opacity-80">
                  <span className="inline-flex items-center justify-center h-6 w-6 rounded-full" style={{ backgroundColor: '#25D366' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="h-4 w-4"><path d="M12 2a10 10 0 00-8.94 14.5L2 22l5.6-1.47A10 10 0 1012 2zm0 2a8 8 0 00-6.93 11.76l.3.52-.35 2.1 2.15-.57.5.29A8 8 0 1012 4zm4.74 8.61c-.27-.14-1.6-.79-1.85-.88-.25-.09-.43-.14-.6.14-.18.27-.69.88-.85 1.06-.16.18-.31.2-.58.07-.27-.14-1.15-.42-2.2-1.34-.81-.72-1.35-1.6-1.51-1.87-.16-.27-.02-.42.12-.56.12-.12.27-.31.4-.47.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.6-1.45-.83-1.99-.21-.5-.43-.43-.6-.43h-.51c-.18 0-.48.07-.74.34-.25.27-.97.95-.97 2.31 0 1.36 1 2.68 1.14 2.86.14.18 1.97 3.01 4.77 4.21.67.29 1.19.46 1.6.59.67.21 1.28.18 1.76.11.54-.08 1.6-.65 1.82-1.28.22-.63.22-1.17.15-1.28-.07-.11-.25-.18-.52-.32z"/></svg>
                  </span>
                  <span>WhatsApp: 5572670701</span>
                </a>
              </div>
              <div className="flex items-center gap-4">
                <a href="https://www.instagram.com/fashion_style_1977?igsh=bnY0b3EwZHduNnhk&utm_source=qr" aria-label="Instagram" className="inline-flex items-center gap-2 hover:opacity-80">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6"><path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm10 2H7a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3zm-5 3a5 5 0 110 10 5 5 0 010-10zm6.5-.75a1.25 1.25 0 110 2.5 1.25 1.25 0 010-2.5z"/></svg>
                  <span>fashion_style_19770</span>
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
